import {
  CollabCalculationTypes,
  CollabCardWidth,
  type CollabCoverField,
  CollabCoverFieldType,
  CollabLayoutType,
  type DatabaseViewJSON,
  type MutableView,
  type View
} from '@databases/model/views/view';
import { type CollabFilterJSON } from '@databases/model/views/view-filter';
import { type CollabGroupingJSON } from '@databases/model/views/view-grouping';
import { type CollabViewSortType } from '@databases/model/views/view-sort';
import { type Op } from 'quill-delta';
import * as Y from 'yjs';

import { type DefaultValueConverters } from '../default-value-converters.ts';
import { SynchronizedRecord } from '../helpers/synchronized-record.ts';

import { type YFilter, filterMapper, yFilterFromJSON } from './y-filter.ts';
import { YGrouping } from './y-grouping.ts';
import { YSortArray } from './y-sort.ts';

const Properties = {
  name: 'n',
  type: 't', // Layout type

  fields: 'fie', // Field visibility
  filter: 'fil',
  sort: 'so',
  sorts: 'vs',
  grouping: 'grp',

  // Board or Gallery
  cardWidth: 'cw',
  coverField: 'cf',
  showFieldNames: 'sfn',
  // Board layout
  groupFieldId: 'gfi',
  // Table layout
  calculations: 'ca',
  showNumberedEntries: 'sne',
  // Calendar layout
  dateFieldId: 'dfi',
  // Extension layout modules
  properties: 'p'
};

/**
 * Frozen governance surface over {@link Properties}. Exposed **only** so the
 * no-new-columns test can fail when a storage key changes.
 *
 * Layout-specific settings must live in the `properties` bag via an
 * extension-layout module — do **not** add a new first-class YView column for a
 * layout setting. Adding a key here trips `y-view.governance.test.ts`, forcing a
 * conscious review; only genuinely cross-cutting view fields belong on YView.
 */
// Keep this a spread of `Properties` (derived, never hardcoded) so that adding
// a storage key is reflected here and trips the no-new-columns governance test.
export const VIEW_STORAGE_PROPERTY_KEYS = Object.freeze({ ...Properties });

const DEFAULT_FIELD_VISIBILITY_ID = 'default';

const storageToSort = (o: object | undefined): CollabViewSortType | undefined => {
  if (o) {
    return {
      ascending: 'a' in o ? Boolean(o.a) : false,
      fieldId: 'f' in o ? String(o.f) : ''
    };
  }
  return undefined;
};

const sortToStorage = (sort: CollabViewSortType | undefined) => {
  if (sort) {
    return {
      a: 'ascending' in sort ? Boolean(sort.ascending) : false,
      f: 'fieldId' in sort ? String(sort.fieldId) : ''
    };
  }
  return undefined;
};

enum YType {
  TABLE = 't',
  GALLERY = 'g',
  BOARD = 'b',
  CALENDAR = 'cal',
  TIMELINE = 'timeline',
  /** @deprecated Card is replaced by Gallery */
  CARD = 'c'
}

const TypeMapping = {
  [CollabLayoutType.TABLE]: 't',
  [CollabLayoutType.GALLERY]: 'g',
  [CollabLayoutType.BOARD]: 'b',
  [CollabLayoutType.CALENDAR]: 'cal',
  [CollabLayoutType.TIMELINE]: 'timeline',
  /** @deprecated Card is replaced by Gallery */
  [CollabLayoutType.CARD]: 'c'
};

const YTypeMapping = {
  [YType.TABLE]: CollabLayoutType.TABLE,
  [YType.GALLERY]: CollabLayoutType.GALLERY,
  [YType.BOARD]: CollabLayoutType.BOARD,
  [YType.CALENDAR]: CollabLayoutType.CALENDAR,
  [YType.TIMELINE]: CollabLayoutType.TIMELINE,
  /** @deprecated Card is replaced by Gallery */
  [YType.CARD]: CollabLayoutType.CARD
};

/**
 * String-keyed views of the layout mappings above. A `Map` lookup returns
 * `undefined` for unknown keys, letting extension layout ids pass through
 * verbatim without unsafe casts on the enum-keyed record.
 *
 * Built-in layouts are stored as their compact code (`t`, `g`, `b`, `cal`,
 * `c`); other layout ids are stored verbatim in the same field. That shared
 * namespace means an id equal to a compact code would be silently decoded here
 * as the built-in — so the compact codes (and only the codes) are reserved:
 * module registration rejects them via `RESERVED_LAYOUT_STORAGE_CODES` in
 * `@databases/view-layout-modules` (enforced by `defineLayoutModule` /
 * `createViewLayoutRegistry`). Canonical layout names are NOT reserved: a
 * built-in never persists as its own name, so a module may register under a
 * canonical name to own that layout's typed settings (e.g. `timeline`, which
 * persists verbatim as `timeline`). Keep `RESERVED_LAYOUT_STORAGE_CODES` in
 * sync with {@link TypeMapping} / {@link YTypeMapping} if a built-in code
 * changes.
 */
const storageByLayoutType = new Map<string, string>(Object.entries(TypeMapping));
const layoutTypeByStorage = new Map<string, CollabLayoutType>(
  Object.entries(YTypeMapping)
);

enum YCardWidth {
  SMALL = 's',
  MEDIUM = 'm',
  LARGE = 'l'
}
const YCardWidthValues = Object.values(YCardWidth);

const CardWidthMapping = {
  [CollabCardWidth.SMALL]: 's',
  [CollabCardWidth.MEDIUM]: 'm',
  [CollabCardWidth.LARGE]: 'l'
};

const YCardWidthMapping = {
  [YCardWidth.SMALL]: CollabCardWidth.SMALL,
  [YCardWidth.MEDIUM]: CollabCardWidth.MEDIUM,
  [YCardWidth.LARGE]: CollabCardWidth.LARGE
};

enum YCoverFieldTypes {
  NONE = 'n',
  DEFAULT = 'd',
  ID = 'i'
}

const storageToCoverField = (o: object): CollabCoverField => {
  const type = 't' in o ? o.t : YCoverFieldTypes.DEFAULT;
  switch (type) {
    case YCoverFieldTypes.ID:
      return { type: CollabCoverFieldType.ID, id: 'i' in o ? String(o.i) : '' };
    case YCoverFieldTypes.NONE:
      return { type: CollabCoverFieldType.None };
    default:
    case YCoverFieldTypes.DEFAULT:
      return { type: CollabCoverFieldType.Default };
  }
};

const coverFieldToStorage = (coverField: CollabCoverField) => {
  switch (coverField.type) {
    case CollabCoverFieldType.Default:
      return { t: YCoverFieldTypes.DEFAULT };
    case CollabCoverFieldType.None:
      return { t: YCoverFieldTypes.NONE };
    case CollabCoverFieldType.ID:
      return { t: YCoverFieldTypes.ID, i: coverField.id };
  }
};

/**
 * Creates the authoritative collaborative store for layout-module-owned
 * properties while keeping the external model shape as plain JSON.
 */
const createPropertiesStore = (
  properties: Record<string, unknown> | undefined
): Y.Map<unknown> =>
  new Y.Map<unknown>(properties ? Object.entries(properties) : undefined);

export class YView extends SynchronizedRecord implements View, MutableView {
  #fields: Y.Map<any> | undefined;
  #filter: YFilter | undefined;
  #grouping: YGrouping | undefined;
  #calculations: Y.Map<any>;
  #sorts: YSortArray;

  constructor(
    input: Y.Map<any>,
    private defaultValueConverters: DefaultValueConverters
  ) {
    super(input);
    input.observe(this.onValueChanged);
    const fieldsRef = input.get(Properties.fields);
    if (fieldsRef instanceof Y.Map) {
      this.#fields = fieldsRef;
    }

    const filterRef = input.get(Properties.filter);
    if (filterRef instanceof Y.Map) {
      this.#filter = filterMapper(filterRef, defaultValueConverters);
    }

    const groupingRef = input.get(Properties.grouping);
    if (groupingRef instanceof Y.Map) {
      this.#grouping = new YGrouping(groupingRef);
    }

    const calculationsRef = input.get(Properties.calculations);
    if (calculationsRef instanceof Y.Map) {
      this.#calculations = calculationsRef;
    } else {
      this.#calculations = new Y.Map();
    }

    const sortsRef = input.get(Properties.sorts);
    if (sortsRef instanceof Y.Array) {
      this.#sorts = new YSortArray(sortsRef);
    } else {
      this.#sorts = new YSortArray(new Y.Array());
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(Properties.fields)) {
      const val = this.data.get(Properties.fields);
      if (val instanceof Y.Map) {
        this.#fields = val;
      } else if (this.#fields) {
        this.#fields = undefined;
      }
    }

    if (e.keysChanged.has(Properties.filter)) {
      const val = this.data.get(Properties.filter);
      if (val instanceof Y.Map) {
        this.#filter = filterMapper(val, this.defaultValueConverters);
      } else if (this.#filter) {
        this.#filter = undefined;
      }
    }

    if (e.keysChanged.has(Properties.grouping)) {
      const val = this.data.get(Properties.grouping);
      if (val instanceof Y.Map) {
        this.#grouping = new YGrouping(val);
      } else if (this.#grouping) {
        this.#grouping = undefined;
      }
    }
  };

  get name(): string {
    return this.collabName()?.toString() ?? '';
  }

  private collabName(): Y.Text | undefined {
    return this.getText(Properties.name);
  }

  private initializeName() {
    if (!this.collabName()) {
      this.set(Properties.name, new Y.Text());
    }
  }

  applyNameDelta(delta: Op[]): void {
    this.initializeName();
    this.collabName()!.applyDelta(delta);
  }

  get sort(): CollabViewSortType | undefined {
    return storageToSort(this.getObject(Properties.sort, undefined));
  }

  set sort(data: CollabViewSortType | undefined) {
    this.set(Properties.sort, sortToStorage(data));
  }

  initializeSorts(): void {
    if (!this.data.has(Properties.sorts)) {
      this.#sorts = this.#sorts || new YSortArray(new Y.Array());
      this.set(Properties.sorts, this.#sorts.unwrap());
    }
  }

  get sorts(): YSortArray {
    return this.#sorts;
  }

  /**
   * Returns the layout type for this view. Falls back to the raw string
   * when the value is not a known {@link CollabLayoutType}, allowing
   * extension layout types to pass through without data loss.
   */
  get layoutType(): CollabLayoutType | string {
    const raw = this.getString(Properties.type, YType.TABLE);

    return layoutTypeByStorage.get(raw) ?? raw;
  }

  /**
   * Stores the layout type in the collaborative document. Built-in
   * types are mapped to their compact Y.Map representation; extension
   * layout strings are stored verbatim.
   */
  set layoutType(data: CollabLayoutType | string) {
    this.set(Properties.type, storageByLayoutType.get(data) ?? data);
  }

  get cardWidth(): CollabCardWidth {
    return YCardWidthMapping[
      this.getStringEnum(Properties.cardWidth, YCardWidthValues, YCardWidth.MEDIUM)
    ];
  }

  set cardWidth(data: CollabCardWidth) {
    this.set(Properties.cardWidth, CardWidthMapping[data]);
  }

  get coverField(): CollabCoverField {
    return storageToCoverField(
      this.getObject(Properties.coverField, { t: YCoverFieldTypes.DEFAULT })
    );
  }

  set coverField(data: CollabCoverField) {
    this.set(Properties.coverField, coverFieldToStorage(data));
  }

  get showFieldNames(): boolean {
    return this.getBoolean(Properties.showFieldNames, true);
  }

  set showFieldNames(data: boolean) {
    this.set(Properties.showFieldNames, data);
  }

  get groupFieldId(): string | undefined {
    return this.getString(Properties.groupFieldId, undefined);
  }

  set groupFieldId(data: string | undefined) {
    this.set(Properties.groupFieldId, data);
  }

  get dateFieldId(): string | undefined {
    return this.getString(Properties.dateFieldId, undefined);
  }

  set dateFieldId(data: string | undefined) {
    this.set(Properties.dateFieldId, data);
  }

  get showNumberedEntries(): boolean {
    return this.getBoolean(Properties.showNumberedEntries, false);
  }

  set showNumberedEntries(data: boolean) {
    this.set(Properties.showNumberedEntries, data);
  }

  /**
   * Opaque properties owned by registered layout modules.
   *
   * YView intentionally does not interpret this object or expose layout-specific
   * accessors. Module-aware SDK/collab paths validate and mutate the shape, and
   * core collab only round-trips it for durable read/write compatibility.
   */
  get properties(): Record<string, unknown> | undefined {
    const properties = this.getStorageValue(Properties.properties);

    if (properties instanceof Y.Map) {
      return properties.toJSON() as Record<string, unknown>;
    }

    return undefined;
  }

  /**
   * Replaces opaque module properties after a registry-aware write path accepts
   * the mutation.
   *
   * Do not add Timeline-style setters here. New layouts should expose typed
   * helpers from their own module and write through the generic extension seam.
   */
  set properties(data: Record<string, unknown> | undefined) {
    this.set(Properties.properties, data ? createPropertiesStore(data) : undefined);
  }

  /**
   * Applies a key-level patch to layout-module-owned properties without
   * replacing the nested Y.Map, so concurrent edits can merge per key.
   */
  updateProperties({
    set,
    unset
  }: {
    set?: Record<string, unknown>;
    unset?: readonly string[];
  }): void {
    const hasValuesToSet = set && Object.keys(set).length > 0;
    const hasValuesToUnset = unset && unset.length > 0;

    if (!hasValuesToSet && !hasValuesToUnset) {
      return;
    }

    this.transactPropertiesPatch(() => {
      const properties = this.getOrCreateProperties();

      Object.entries(set ?? {}).forEach(([key, value]) => properties.set(key, value));
      unset?.forEach((key) => properties.delete(key));

      if (properties.size === 0) {
        this.set(Properties.properties, undefined);
      }
    });
  }

  /**
   * Groups all writes for one property patch into a single Yjs
   * transaction so observers never process a partial property bag.
   */
  private transactPropertiesPatch(update: () => void): void {
    if (this.data.doc) {
      this.data.doc.transact(update);

      return;
    }

    update();
  }

  /**
   * Returns the existing layout property store, or initializes it when a
   * partial patch is the first write to the property bag.
   */
  private getOrCreateProperties(): Y.Map<unknown> {
    const existingProperties = this.getStorageValue(Properties.properties);

    if (existingProperties instanceof Y.Map) {
      return existingProperties;
    }

    const properties = createPropertiesStore(undefined);

    this.set(Properties.properties, properties);

    return properties;
  }

  get fields(): Record<string, boolean> | undefined {
    return this.#fields
      ? { ...this.#fields.toJSON(), [DEFAULT_FIELD_VISIBILITY_ID]: false }
      : undefined;
  }

  get filter() {
    return this.#filter;
  }

  get grouping() {
    return this.#grouping;
  }

  isCollabCalculationType(value: string): value is CollabCalculationTypes {
    return Object.values(CollabCalculationTypes).includes(
      value as CollabCalculationTypes
    );
  }

  get calculations(): Record<string, CollabCalculationTypes> {
    const preValidatedData = { ...this.#calculations.toJSON() };

    const validatedData = {} as Record<string, CollabCalculationTypes>;
    Object.keys(preValidatedData).forEach((key) => {
      const maybeCollabCalculationType = preValidatedData[key];
      if (this.isCollabCalculationType(maybeCollabCalculationType)) {
        validatedData[key] = maybeCollabCalculationType;
      }
    });

    return validatedData;
  }

  setCalculation(fieldKey: string, value: CollabCalculationTypes) {
    this.#calculations.set(fieldKey, value);
    this.set(Properties.calculations, this.#calculations);
  }

  removeCalculation(fieldKey: string) {
    this.#calculations.delete(fieldKey);
    this.set(Properties.calculations, this.#calculations);
  }

  setFilter(val: CollabFilterJSON) {
    const yfilter = yFilterFromJSON(val, this.defaultValueConverters);
    this.set(Properties.filter, yfilter.data);
    this.#filter = yfilter;
    return yfilter;
  }

  clearFilter() {
    this.#filter = undefined;
    this.set(Properties.filter, undefined);
  }

  initGrouping(): YGrouping {
    return this.setGrouping({});
  }

  setGrouping(value: CollabGroupingJSON): YGrouping {
    const yGrouping = YGrouping.fromJSON(value);
    this.set(Properties.grouping, yGrouping.data);
    this.#grouping = yGrouping;
    return yGrouping;
  }

  clearGrouping() {
    this.#grouping = undefined;
    this.set(Properties.grouping, undefined);
  }

  setFieldVisibility(
    fieldKey: string,
    fieldValue: boolean,
    dbFields: readonly string[],
    options: { includeNewFieldInValidation?: boolean } = {
      includeNewFieldInValidation: false
    }
  ): void {
    if (this.#fields) {
      this.#fields.set(fieldKey, fieldValue);
    } else {
      this.#fields = new Y.Map([[fieldKey, fieldValue]]);
      dbFields.forEach((fieldId) => {
        fieldId !== fieldKey && this.#fields?.set(fieldId, true);
      });
    }
    this.set(Properties.fields, this.#fields);
    if (options.includeNewFieldInValidation) {
      this.validateFields([...dbFields, fieldKey]);
    } else {
      this.validateFields(dbFields);
    }
  }

  removeFieldVisibilityField(fieldKey: string, dbFields: readonly string[]): void {
    if (this.#fields) {
      this.#fields.delete(fieldKey);
    }
    this.validateFields(dbFields);
  }

  resetFieldVisibility(): void {
    this.set(Properties.fields, undefined);
    this.#fields = undefined;
  }

  private validateFields(dbFields: readonly string[]): void {
    if (this.#fields) {
      Object.keys(this.#fields.toJSON()).forEach((key) => {
        if (dbFields.indexOf(key) === -1) {
          this.#fields!.delete(key);
        }
      });

      if (dbFields.every((key) => this.#fields?.get(key))) {
        this.set(Properties.fields, undefined);
        this.#fields = undefined;
      }
    }
  }

  public toJSON(): DatabaseViewJSON {
    return {
      name: this.name,
      sort: this.sort,
      layoutType: this.layoutType,
      cardWidth: this.cardWidth,
      coverField: this.coverField,
      showFieldNames: this.showFieldNames,
      groupFieldId: this.groupFieldId,
      dateFieldId: this.dateFieldId,
      showNumberedEntries: this.showNumberedEntries,
      ...(this.properties && { properties: this.properties }),
      fields: this.fields,
      filter: this.filter ? this.filter.toJSON() : undefined,
      calculations: this.calculations,
      sorts: this.sorts ? this.sorts.toArray() : [],
      grouping: this.grouping?.toJSON()
    };
  }

  static fromJSON(
    data: DatabaseViewJSON,
    defaultValueConverters: DefaultValueConverters
  ): YView {
    return new YView(
      new Y.Map([
        [Properties.name, new Y.Text(data.name)],
        [Properties.sort, data.sort ? sortToStorage(data.sort) : undefined],
        [Properties.type, storageByLayoutType.get(data.layoutType) ?? data.layoutType],
        [Properties.cardWidth, CardWidthMapping[data.cardWidth]],
        [Properties.coverField, coverFieldToStorage(data.coverField)],
        [Properties.showFieldNames, data.showFieldNames],
        [Properties.groupFieldId, data.groupFieldId],
        [Properties.dateFieldId, data.dateFieldId],
        [Properties.showNumberedEntries, data.showNumberedEntries],
        ...(data.properties
          ? ([[Properties.properties, createPropertiesStore(data.properties)]] as const)
          : []),
        [
          Properties.fields,
          data.fields ? new Y.Map(Object.entries(data.fields)) : undefined
        ],
        [
          Properties.filter,
          data.filter
            ? yFilterFromJSON(data.filter, defaultValueConverters).data
            : undefined
        ],
        [Properties.calculations, new Y.Map(Object.entries(data.calculations ?? {}))],
        [Properties.sorts, YSortArray.fromJSON(data.sorts ?? []).unwrap()],
        [
          Properties.grouping,
          data.grouping ? YGrouping.fromJSON(data.grouping).data : undefined
        ]
      ]),
      defaultValueConverters
    );
  }
}
